import React, { useEffect, useRef, useState } from 'react';
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { PieceCrop } from "./PieceCrop";

export const ImageWithBbox = ({ src, pieces }) => {
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  const [naturalSize, setNaturalSize] = useState({ width: null, height: null });
  const [selectedPiece, setSelectedPiece] = useState(null);

  useEffect(() => {
    const img = imgRef.current;

    if (!img) return;

    const handleLoad = () => {
      setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
    };

    if (img.complete) {
      handleLoad();
    } else {
      img.addEventListener('load', handleLoad);
      return () => img.removeEventListener('load', handleLoad);
    }
  }, [src]);

  const computePosition = (point) => {
    const img = imgRef.current;
    const container = containerRef.current;

    if (!img || !container || !naturalSize.width || !naturalSize.height)
      return;

    const containerRect = container.getBoundingClientRect();
    const naturalRatio = naturalSize.width / naturalSize.height;
    const containerRatio = containerRect.width / containerRect.height;

    let offsetX = 0;
    let offsetY = 0;
    let visibleWidth = containerRect.width;
    let visibleHeight = containerRect.height;

    if (naturalRatio > containerRatio) {
      const scale = containerRect.height / naturalSize.height;
      visibleWidth = naturalSize.width * scale;
      offsetX = (containerRect.width - visibleWidth) / 2;
    } else {
      const scale = containerRect.width / naturalSize.width;
      visibleHeight = naturalSize.height * scale;
      offsetY = (containerRect.height - visibleHeight) / 2;
    }

    const x = offsetX + (point[0] / naturalSize.width) * visibleWidth;
    const y = offsetY + (point[1] / naturalSize.height) * visibleHeight;

    return {x, y};
  };

  const _computePiecePosition = (piece) => {
    let position = computePosition([piece.x1, piece.y1]);
    if (!position) return null;
    const {x: x1, y: y1} = position;

    position = computePosition([piece.x2, piece.y2]);
    if (!position) return null;
    const {x: x2, y: y2} = position;

    return { x1, y1, x2, y2 };
  };

  const styles = {
    bboxCorner: {
      position: 'absolute',
      width: '12px',
      height: '12px',
      border: '3px solid red',
      borderRadius: '5px',
      zIndex: 60,
      pointerEvents: 'none',
      transform: 'translate(-50%, -50%)',
    },
  };

  return (
    <div ref={containerRef} style={{position: 'relative', width: '100%', height: '100%', overflow: 'hidden'}}>
      <img ref={imgRef} src={src} alt="overlay" style={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}}/>

      <div className="absolute top-0 left-0 w-full h-full">
        {
          pieces.map((piece, index) => {
            const piecePosition = _computePiecePosition(piece);
            if (!piecePosition) return null;
            const {x1, y1, x2, y2} = piecePosition;

            const bgColor = piece.conf < 0.5 ? 'bg-orange-500/25' : piece.conform ? 'bg-green-500/25' : 'bg-red-500/25';
            const borderColor = piece.conf < 0.5 ? 'orange' : piece.conform ? 'green' : 'red';

            return (
              <React.Fragment key={index}>
                <div className={`${bgColor} absolute rounded`}
                     style={{ left: `${x1}px`, top: `${y1}px`, width: `${x2 - x1}px`, height: `${y2 - y1}px`}}
                     onClick={() => setSelectedPiece(piece)}
                >
                </div>

                <div style={{...styles.bboxCorner, borderBottom: 'none', borderRight: 'none', borderBottomLeftRadius: 0, borderTopRightRadius: 0,
                  left: `${x1}px`, top: `${y1}px`, borderColor: borderColor }} />
                <div style={{...styles.bboxCorner, borderBottom: 'none', borderLeft: 'none', borderBottomRightRadius: 0, borderTopLeftRadius: 0,
                  left: `${x2}px`, top: `${y1}px`, borderColor: borderColor }} />
                <div style={{...styles.bboxCorner, borderTop: 'none', borderRight: 'none', borderBottomRightRadius: 0, borderTopLeftRadius: 0,
                  left: `${x1}px`, top: `${y2}px`, borderColor: borderColor }} />
                <div style={{...styles.bboxCorner, borderTop: 'none', borderLeft: 'none', borderBottomLeftRadius: 0, borderTopRightRadius: 0,
                  left: `${x2}px`, top: `${y2}px`, borderColor: borderColor }} />

                {
                  !piece.conform &&
                    <div className="flex justify-center items-center absolute w-5 h-5 rounded-full text-xs text-white p-0.5"
                         style={{left: `${x2 - 25}px`, top: `${y2 - 25}px`, backgroundColor: borderColor}}>
                      <ExclamationTriangleIcon/>
                    </div>
                }
              </React.Fragment>
            )
          })
        }
      </div>

      {
        selectedPiece &&
          <div className="absolute top-0 left-0 bottom-0 right-0 bg-white/60 z-[70]" onClick={() => setSelectedPiece(null)}>
            <PieceCrop src={src} piece={selectedPiece} margin={0.}/>
          </div>
      }
    </div>
  );
};
